import React, { useState } from 'react';
import { api } from '../services/api';
import Navbar from '../components/Navbar';
import DisclaimerBanner from '../components/DisclaimerBanner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Star, MessageSquare, Bug, Lightbulb, Send, Loader2, CheckCircle, Target, Sparkles } from 'lucide-react';
import { toast } from 'sonner';

const StarRating = ({ value, onChange, label, description }) => {
  const [hover, setHover] = useState(0);
  
  const getLabel = (val) => {
    if (val === 5) return 'Excellent';
    if (val === 4) return 'Great';
    if (val === 3) return 'Good';
    if (val === 2) return 'Fair';
    if (val === 1) return 'Poor';
    return '';
  };
  
  return (
    <div className="space-y-2">
      <div>
        <Label className="text-base">{label}</Label>
        {description && <p className="text-sm text-slate-500">{description}</p>}
      </div>
      <div className="flex items-center gap-2">
        <div className="flex gap-1">
          {[1, 2, 3, 4, 5].map((star) => (
            <button
              key={star}
              type="button"
              onClick={() => onChange(star)}
              onMouseEnter={() => setHover(star)}
              onMouseLeave={() => setHover(0)}
              className="p-1 transition-transform hover:scale-110"
            >
              <Star
                className={`h-8 w-8 transition-colors ${
                  star <= (hover || value)
                    ? 'fill-amber-400 text-amber-400'
                    : 'text-slate-300'
                }`}
              />
            </button>
          ))}
        </div>
        {value > 0 && (
          <span className="text-sm text-slate-500 ml-2">{getLabel(value)}</span>
        )}
      </div>
    </div>
  );
};

const FeedbackPage = () => {
  const [activeTab, setActiveTab] = useState('rating');
  const [appRating, setAppRating] = useState(0);
  const [accuracyRating, setAccuracyRating] = useState(0);
  const [content, setContent] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmitRating = async () => {
    if (appRating === 0 && accuracyRating === 0) {
      toast.error('Please provide at least one rating');
      return;
    }

    setLoading(true);
    try {
      await api.submitFeedback({
        feedback_type: 'rating',
        app_rating: appRating || null,
        accuracy_rating: accuracyRating || null,
        content: content.trim() || null
      });
      setSubmitted(true);
      toast.success('Thank you for your feedback!');
    } catch (err) {
      toast.error('Failed to submit feedback');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitOther = async (feedbackType) => {
    if (!content.trim()) {
      toast.error('Please provide some feedback');
      return;
    }

    setLoading(true);
    try {
      await api.submitFeedback({
        feedback_type: feedbackType,
        content: content.trim()
      });
      setSubmitted(true);
      toast.success('Thank you for your feedback!');
    } catch (err) {
      toast.error('Failed to submit feedback');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setAppRating(0);
    setAccuracyRating(0);
    setContent('');
    setSubmitted(false);
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
        <Navbar />
        <main className="pt-20 pb-16 main-content">
          <div className="max-w-2xl mx-auto px-4 py-12">
            <Card className="border-slate-200">
              <CardContent className="p-8 text-center">
                <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-6">
                  <CheckCircle className="h-8 w-8 text-emerald-600" />
                </div>
                <h2 className="text-2xl font-semibold text-slate-900 mb-2">Thank You!</h2>
                <p className="text-slate-600 mb-6">
                  Your feedback helps us build AI-powered medical insights that clinicians can trust.
                </p>
                <Button onClick={resetForm} variant="outline" className="rounded-full">
                  Submit More Feedback
                </Button>
              </CardContent>
            </Card>
          </div>
        </main>
        <DisclaimerBanner />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      <Navbar />
      <main className="pt-20 pb-16 main-content">
        <div className="max-w-2xl mx-auto px-4 py-12">
          <div className="text-center mb-8">
            <div className="w-12 h-12 rounded-xl bg-slate-100 flex items-center justify-center mx-auto mb-4">
              <MessageSquare className="h-6 w-6 text-slate-600" />
            </div>
            <h1 className="text-3xl font-semibold text-slate-900 mb-2">Feedback</h1>
            <p className="text-slate-600">
              Help us improve NoSpin-EBM for everyone
            </p>
          </div>

          <Card className="border-slate-200">
            <CardContent className="p-6">
              <Tabs value={activeTab} onValueChange={setActiveTab}>
                <TabsList className="grid grid-cols-3 bg-slate-100 rounded-lg p-1 mb-6">
                  <TabsTrigger value="rating" className="rounded-md flex items-center gap-1.5" data-testid="tab-rating">
                    <Star className="h-4 w-4" />
                    Rate
                  </TabsTrigger>
                  <TabsTrigger value="suggestion" className="rounded-md flex items-center gap-1.5" data-testid="tab-suggestion">
                    <Lightbulb className="h-4 w-4" />
                    Suggest
                  </TabsTrigger>
                  <TabsTrigger value="bug" className="rounded-md flex items-center gap-1.5" data-testid="tab-bug">
                    <Bug className="h-4 w-4" />
                    Bug
                  </TabsTrigger>
                </TabsList>

                <TabsContent value="rating">
                  <div className="space-y-6">
                    {/* App Experience Rating */}
                    <div className="p-4 bg-slate-50 rounded-lg">
                      <div className="flex items-start gap-3 mb-3">
                        <div className="p-2 rounded-lg bg-sky-100">
                          <Sparkles className="h-5 w-5 text-sky-600" />
                        </div>
                        <StarRating
                          value={appRating}
                          onChange={setAppRating}
                          label="How do you like the app?"
                          description="Ease of use, design, features"
                        />
                      </div>
                    </div>

                    {/* Accuracy Rating */}
                    <div className="p-4 bg-slate-50 rounded-lg">
                      <div className="flex items-start gap-3 mb-3">
                        <div className="p-2 rounded-lg bg-emerald-100">
                          <Target className="h-5 w-5 text-emerald-600" />
                        </div>
                        <StarRating
                          value={accuracyRating}
                          onChange={setAccuracyRating}
                          label="How accurate are the results?"
                          description="Quality and relevance of AI recommendations"
                        />
                      </div>
                    </div>

                    <div>
                      <Label>Additional comments (optional)</Label>
                      <Textarea
                        value={content}
                        onChange={(e) => setContent(e.target.value)}
                        placeholder="Any specific feedback? What could be better?"
                        className="mt-2"
                        rows={4}
                        data-testid="rating-comment"
                      />
                    </div>
                    
                    <Button
                      onClick={handleSubmitRating}
                      disabled={loading || (appRating === 0 && accuracyRating === 0)}
                      className="w-full h-11 rounded-full bg-slate-900 hover:bg-slate-800 text-white"
                      data-testid="submit-rating"
                    >
                      {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Send className="h-4 w-4 mr-2" />}
                      Submit Feedback
                    </Button>
                  </div>
                </TabsContent>

                <TabsContent value="suggestion">
                  <div className="space-y-6">
                    <div>
                      <Label>Your suggestion</Label>
                      <Textarea
                        value={content}
                        onChange={(e) => setContent(e.target.value)}
                        placeholder="How can we improve NoSpin-EBM? What features would you like to see?"
                        className="mt-2"
                        rows={6}
                        data-testid="suggestion-text"
                      />
                    </div>
                    <Button
                      onClick={() => handleSubmitOther('suggestion')}
                      disabled={loading || !content.trim()}
                      className="w-full h-11 rounded-full bg-slate-900 hover:bg-slate-800 text-white"
                      data-testid="submit-suggestion"
                    >
                      {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Lightbulb className="h-4 w-4 mr-2" />}
                      Submit Suggestion
                    </Button>
                  </div>
                </TabsContent>

                <TabsContent value="bug">
                  <div className="space-y-6">
                    <div>
                      <Label>Describe the bug</Label>
                      <Textarea
                        value={content}
                        onChange={(e) => setContent(e.target.value)}
                        placeholder="What happened? What were you trying to do? Please be as specific as possible."
                        className="mt-2"
                        rows={6}
                        data-testid="bug-text"
                      />
                    </div>
                    <Button
                      onClick={() => handleSubmitOther('bug')}
                      disabled={loading || !content.trim()}
                      className="w-full h-11 rounded-full bg-red-600 hover:bg-red-700 text-white"
                      data-testid="submit-bug"
                    >
                      {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Bug className="h-4 w-4 mr-2" />}
                      Report Bug
                    </Button>
                  </div>
                </TabsContent>
              </Tabs>
            </CardContent>
          </Card>
        </div>
      </main>
      <DisclaimerBanner />
    </div>
  );
};

export default FeedbackPage;
